import {
    MAX_DATA_ENTRIES,
    state,
    setLoginFlag,
    resetPage,
    setCredits,
    normalizeJobID,
    isValidJobID,
    impersonationQuery,
    setViewAsEmail,
    loadSavedViewAs,
    updateUserDisplay,
    setTeams,
    setActiveTeamId,
    hydrateProjectFocus,
    clearClientAuthState,
} from './globals.js';

const apiQueue = [];
let isProcessing = false;
let projectsCache = null;
let projectsCacheAt = 0;
let projectsCacheScope = '';
const PROJECTS_CACHE_TTL_MS = 5 * 60 * 1000;

function currentProjectsCacheScope() {
    const email = String(state.email || '').trim().toLowerCase();
    return `${email}|${state.isOwner ? 'owner' : 'user'}|${impersonationQuery()}`;
}

function clearProjectsCache() {
    projectsCache = null;
    projectsCacheAt = 0;
    projectsCacheScope = '';
}

export function withImpersonation(url) {
    const qs = impersonationQuery();
    if (!qs) return url;
    return url.includes('?') ? `${url}&${qs}` : `${url}?${qs}`;
}

function triggerDownload(url, filename = '', { target = '', rel = '' } = {}) {
    const link = document.createElement('a');
    link.href = url;
    if (filename) link.download = filename;
    if (target) link.target = target;
    if (rel) link.rel = rel;
    document.body.appendChild(link);
    try { link.click(); } catch (err) { console.warn('download failed', err); }
    document.body.removeChild(link);
}

async function processQueue() {
    if (isProcessing || apiQueue.length === 0) return;
    
    isProcessing = true;

    while (apiQueue.length > 0) {
        const { func, resolve, reject } = apiQueue.shift();
        try {
            const result = await func();
            resolve(result);
        } catch (error) {
            console.error('API call failed:', error);
            reject(error);
        }

        // Enforce the wait time between calls
        await new Promise(resolve => setTimeout(resolve, state.api_wait_ms));
    }

    isProcessing = false;
}

export function enqueueApiCall(func) {
    return new Promise((resolve, reject) => {
        apiQueue.push({ func, resolve, reject });
        processQueue(); // Start processing if not already running
    });
}

function runReadApiCall(func) {
    return Promise.resolve().then(func);
}

async function readApiData(response, fallbackMessage = 'Request failed', defaultValue = {}) {
    let payload = null;
    try {
        payload = await response.json();
    } catch (_) {
        throw new Error(`Invalid API response (HTTP ${response.status})`);
    }
    if (!response.ok || !payload || !payload.success) {
        const error = new Error((payload && payload.error) || `${fallbackMessage} (HTTP ${response.status})`);
        if (payload && payload.data !== undefined) error.data = payload.data;
        throw error;
    }
    return payload.data ?? defaultValue;
}

function getApiData(url, fallbackMessage, defaultValue = {}) {
    return runReadApiCall(async () => {
        const res = await fetch(url, { method: 'GET', headers: { 'Content-Type': 'application/json' } });
        return readApiData(res, fallbackMessage, defaultValue);
    });
}

async function getProtectedApiData(url, fallbackMessage = 'Request failed', defaultValue = {}) {
    const response = await fetch(url, { method: 'GET' });
    if (response.status === 401) throw new Error('Login required');
    if (response.status === 403) throw new Error('Access denied');
    const payload = await response.json();
    if (!payload.success) throw new Error(payload.error || fallbackMessage);
    return payload.data ?? defaultValue;
}

async function postApiData(url, body, fallbackMessage = 'Request failed', defaultValue = {}, { impersonate = true } = {}) {
    const isFormData = (typeof FormData !== 'undefined') && (body instanceof FormData);
    let options = { method: 'POST' };
    if (isFormData) {
        options = { method: 'POST', body };
    } else if (body !== undefined) {
        options = { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body || {}) };
    }
    const res = await fetch(impersonate ? withImpersonation(url) : url, options);
    return readApiData(res, fallbackMessage, defaultValue);
}

// Convenience: dataset info via queue
export async function getDatasetInfo(digest) {
    if (!digest) throw new Error('digest required');
    return getApiData(
        withImpersonation(`/api/v1/dataset/info?digest=${encodeURIComponent(digest)}`),
        'Failed to load dataset'
    );
}

// Convenience: dataset info via name
export async function getDatasetInfoByName(name, project) {
    if (!name) throw new Error('dataset name required');
    return getApiData(
        withImpersonation(`/api/v1/dataset/info?name=${encodeURIComponent(name)}`),
        'Failed to load dataset'
    );
}

// Project primary dataset: GET returns {exists, files, meta} (dataset/info shape)
export async function getProjectPrimaryDataset(project) {
    const name = String(project || '').trim();
    if (!name) throw new Error('project required');
    return getApiData(withImpersonation(`/api/v1/project/primary-dataset?project=${encodeURIComponent(name)}`), 'Failed to load project files');
}

const TEXT_MIME_HINTS = ['text/', 'json', 'xml', 'yaml', 'yml', 'toml', 'csv', 'tsv', 'html'];

function isMimeTextLike(contentType = '') {
    const lower = String(contentType || '').toLowerCase();
    if (!lower) return false;
    return TEXT_MIME_HINTS.some((token) => lower.includes(token));
}

function bufferLooksBinary(buffer) {
    if (!(buffer instanceof ArrayBuffer)) return false;
    const sampleLength = Math.min(buffer.byteLength, 2048);
    if (sampleLength === 0) return false;
    const bytes = new Uint8Array(buffer, 0, sampleLength);
    let controlCount = 0;
    for (let i = 0; i < bytes.length; i += 1) {
        const value = bytes[i];
        if (value === 0) {
            return true; // embedded NUL strongly suggests binary
        }
        if (value < 0x20 && value !== 0x09 && value !== 0x0A && value !== 0x0D) {
            controlCount += 1;
            if (controlCount > bytes.length / 6) {
                return true; // too many control chars for plain text
            }
        }
    }
    return false;
}

export async function fetchDatasetFile(digest, path) {
    if (!digest || !path) throw new Error('digest and path required');
    const url = withImpersonation(`/api/v1/dataset/file?digest=${encodeURIComponent(digest)}&path=${encodeURIComponent(path)}`);
    return fetchApiFile(url, { parseLegacyJsonPayload: true });
}

export async function getJobInfo(jobid) {
    const normalized = normalizeJobID(jobid);
    if (!isValidJobID(normalized)) {
        throw new Error('valid job id required');
    }
    return getApiData(withImpersonation(`/api/v1/job/view?jobid=${normalized}`), 'Failed to load job info');
}

export async function getJobResults(jobid) {
    const normalized = normalizeJobID(jobid);
    if (!isValidJobID(normalized)) {
        throw new Error('valid job id required');
    }
    return getApiData(withImpersonation(`/api/v1/job/results?jobid=${normalized}`), 'Failed to load job results');
}

export async function fetchJobFile(jobid, path) {
    const normalized = normalizeJobID(jobid);
    if (!isValidJobID(normalized) || !path) throw new Error('job id and path required');
    const url = withImpersonation(`/api/v1/job/file?jobid=${normalized}&path=${encodeURIComponent(path)}`);
    return fetchApiFile(url);
}

function fetchApiFile(url, { parseLegacyJsonPayload = false } = {}) {
    return runReadApiCall(async () => {
        const res = await fetch(url, { method: 'GET' });
        if (!res.ok) {
            let message = `Failed to load file (HTTP ${res.status})`;
            try {
                const errText = await res.text();
                if (errText) {
                    try {
                        const errJson = JSON.parse(errText);
                        if (errJson && errJson.error) {
                            message = errJson.error;
                        }
                    } catch (_) {
                        message = errText.trim() || message;
                    }
                }
            } catch (_) {
                // ignore body-read failures
            }
            throw new Error(message);
        }

        const headerTag = res.headers.get('X-Subseq-File');
        const contentType = res.headers.get('Content-Type') || '';
        const lenHeader = res.headers.get('Content-Length');
        let declaredSize = lenHeader ? parseInt(lenHeader, 10) : NaN;
        const buffer = await res.arrayBuffer();
        if (!Number.isFinite(declaredSize) || declaredSize < 0) {
            declaredSize = buffer.byteLength;
        }

        const textDecoder = new TextDecoder('utf-8', { fatal: false });

        if (headerTag === '1') {
            const textLike = isMimeTextLike(contentType) || !bufferLooksBinary(buffer);
            if (!textLike) {
                return { size: declaredSize, contentType, binary: true, buffer };
            }
            const content = textDecoder.decode(buffer);
            return { content, size: declaredSize, contentType, binary: false, buffer };
        }

        const looksBinary = bufferLooksBinary(buffer);
        if (looksBinary) {
            return { size: declaredSize, contentType, binary: true, buffer };
        }

        const fallbackText = textDecoder.decode(buffer);
        if (parseLegacyJsonPayload) {
            try {
                const parsed = JSON.parse(fallbackText);
                if (parsed && typeof parsed === 'object' && parsed.success && parsed.data) {
                    const payload = parsed.data;
                    const content = String(payload.content || '');
                    const size = Number(payload.size || declaredSize || content.length || 0);
                    const type = payload.content_type || contentType || '';
                    const binary = !!payload.binary;
                    if (!binary) {
                        return { content, size, contentType: type, binary: false, buffer };
                    }
                    return { size, contentType: type, binary: true, buffer };
                }
            } catch (_) {
                // not JSON; treat as plain text content
            }
        }
        return {
            content: fallbackText,
            size: declaredSize || fallbackText.length,
            contentType,
            binary: false,
            buffer,
        };
    });
}

export async function getDatasetResults({ digest = '', name = '', project = '' } = {}) {
    const qs = new URLSearchParams();
    if (digest) {
        qs.set('digest', digest);
    } else if (name) {
        qs.set('name', name);
        if (project) qs.set('project', project);
    } else {
        throw new Error('dataset digest or name required');
    }
    return getApiData(withImpersonation(`/api/v1/dataset/results?${qs.toString()}`), 'Failed to load dataset results');
}

export async function reqOTP(email, { resend = false } = {}) {
    const cleanEmail = String(email || '').trim().toLowerCase();
    if (!cleanEmail) return false;

    return enqueueApiCall(async () => {
        try {
            const data = await postApiData('/api/v1/auth/otp', { email: cleanEmail, resend: !!resend }, 'Error', { sent: true }, { impersonate: false });
            state.tempEmail = cleanEmail;
            return data || { sent: true };
        } catch (error) {
            console.error('Error:', error);
            return false;
        }
    });
}

export async function verifyPayment(sessionId, txId) {
    if (!sessionId || !txId) return false;

    return enqueueApiCall(async () => {
        state.redUrl = ""

        try {
            const response = await fetch(`/api/v1/payment/verify?session_id=${sessionId}&tx=${txId}`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' }
            });

            const resp = await response.json();
            if (resp.success) {
                state.success = true;
                state.redUrl = resp.data.url; // Assuming `resp.data` contains any useful info (like redirect URL)
            } else {
                state.error = resp.error || 'Payment verification failed';
                console.error('Payment verification failed:', resp.error);
                return false;
            }
        } catch (error) {
            state.success = false;
            state.error = error.message || 'Error verifying payment';
            console.error('Error attempting to verify payment:', error);
            return false;
        }
    })
}

export async function login(otp) {
    if (!otp) return;

    return enqueueApiCall(async () => {
        try {
            const data = await postApiData('/api/v1/auth/login', { email: state.tempEmail, otp, type: 'web' }, 'Login failed', {}, { impersonate: false });
            if (!data?.expires_at) return;
            setLoginFlag(true, data.expires_at);
            clearProjectsCache();
            resetPage();
        } catch (error) {
            console.log('Login failed:', error?.message || error);
        }
    });
}

async function fetchAccountInfo(url = '/api/v1/account/info') {
    const response = await fetch(url, { method: 'GET' });
    if (response.status === 401) {
        return { unauthorized: true, data: null };
    }
    const resp = await response.json();
    if (!resp || !resp.success) {
        throw new Error((resp && resp.error) || 'Failed to fetch user info');
    }
    return { unauthorized: false, data: resp.data || {} };
}

async function fetchTeamsNow() {
    const response = await fetch('/api/v1/team/list', { method: 'GET' });
    if (response.status === 401) {
        clearClientAuthState();
        clearProjectsCache();
        const err = new Error('Login required');
        err.unauthorized = true;
        throw err;
    }
    const resp = await response.json();
    if (!resp || !resp.success) {
        throw new Error((resp && resp.error) || 'Failed to load teams');
    }
    const teams = (resp.data && Array.isArray(resp.data.teams)) ? resp.data.teams : [];
    setTeams(teams);
    return teams;
}

export async function logout(silent = false) {
    return enqueueApiCall(async () => {
        try {
            if (state.isOwner && state.viewAsEmail) {
                setViewAsEmail('');
                clearProjectsCache();
                const info = await fetchAccountInfo('/api/v1/account/info');
                if (info.data) {
                    state.email = info.data.email || '';
                    setCredits(Number(info.data.credits || 0));
                    updateUserDisplay();
                }
                return true;
            }

            const response = await fetch('/api/v1/account/logout', {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json', // Specify JSON for API interaction
                },
            });

            let result = {};
            try {
                result = await response.json();
            } catch (_) {
                result = {};
            }

            if ((response.ok && result.success) || response.status === 401) {
                clearClientAuthState();
                clearProjectsCache();
                if(!silent){
                    window.location.href = '/';
                }
            } else {
                console.error('Logout failed:', result.error || response.statusText);
            }
        } catch (error) {
            console.error('Error during logout:', error);
        }
    })
}

async function loadUserInfoNow() {
    try {
        let baseInfo;
        try {
            baseInfo = await fetchAccountInfo(withImpersonation('/api/v1/account/info'));
        } catch (infoErr) {
            if (!state.activeTeamId) throw infoErr;
            setActiveTeamId(0);
            clearProjectsCache();
            baseInfo = await fetchAccountInfo('/api/v1/account/info');
        }
        if (baseInfo.unauthorized) {
            clearClientAuthState();
            clearProjectsCache();
            return;
        }

        const baseData = baseInfo.data || {};
        state.email = baseData.email || '';
        state.analyticsAccess = false;
        state.isOwner = false;

        try {
            const extraRes = await fetch('/api/v1/account/extra', { method: 'GET' });
            if (extraRes.ok) {
                const extra = await extraRes.json();
                if (extra && extra.success && extra.data) {
                    state.analyticsAccess = !!extra.data.analytics_access;
                    state.isOwner = !!extra.data.is_owner;
                }
            }
        } catch (extraErr) {
            console.warn('Failed to fetch account extra:', extraErr);
        }

        loadSavedViewAs();
        try {
            await fetchTeamsNow();
        } catch (teamErr) {
            if (teamErr?.unauthorized) return;
            console.warn('Failed to fetch teams:', teamErr);
        }
        if (state.isOwner) {
            window.subseqSetViewAs = async (email) => {
                setViewAsEmail(email);
                clearProjectsCache();
                await loadUserInfo();
            };
        } else {
            delete window.subseqSetViewAs;
            state.viewAsEmail = '';
        }

        let effectiveData = baseData;
        if (state.isOwner && state.viewAsEmail) {
            try {
                const impInfo = await fetchAccountInfo(withImpersonation('/api/v1/account/info'));
                effectiveData = impInfo.data || baseData;
            } catch (impErr) {
                console.warn('Failed to fetch impersonated account info:', impErr);
                setViewAsEmail('');
            }
        }

        state.email = effectiveData.email || '';
        hydrateProjectFocus();
        setCredits(Number(effectiveData.credits || 0));
        updateUserDisplay();
    } catch (error) {
        console.error('Failed to fetch user info:', error);
        state.analyticsAccess = false;
        state.isOwner = false;
        state.viewAsEmail = '';
    }
}

export async function loadUserInfo() {
    return enqueueApiCall(loadUserInfoNow);
}

export async function loadTeams() {
    return enqueueApiCall(fetchTeamsNow);
}

export async function createTeam(name) {
    const clean = String(name || '').trim();
    if (!clean) throw new Error('name required');
    return enqueueApiCall(async () => {
        const response = await fetch('/api/v1/team/new', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name: clean }),
        });
        const resp = await response.json();
        if (!resp || !resp.success) {
            throw new Error((resp && resp.error) || 'Failed to create team');
        }
        await fetchTeamsNow();
        return resp.data || {};
    });
}

export async function getTeamInfo(teamID) {
    const id = Number(teamID || 0);
    if (!id) throw new Error('team_id required');
    return getApiData(`/api/v1/team/info?team_id=${encodeURIComponent(String(id))}`, 'Failed to load team');
}

export async function renameTeam(teamID, name) {
    const id = Number(teamID || 0);
    const clean = String(name || '').trim();
    if (!id || !clean) throw new Error('team_id and name required');
    return enqueueApiCall(async () => {
        await postApiData('/api/v1/team/rename', { team_id: id, name: clean }, 'Failed to rename team');
        await fetchTeamsNow();
        return true;
    });
}

export async function addTeamMember(teamID, email, isTeamAdmin = false) {
    return writeTeamMember('/api/v1/team/member/add', teamID, email, isTeamAdmin);
}

export async function updateTeamMember(teamID, userID, isTeamAdmin = false) {
    const id = Number(teamID || 0);
    const uid = Number(userID || 0);
    if (!id || !uid) throw new Error('team_id and user_id required');
    return enqueueApiCall(async () => {
        await postApiData('/api/v1/team/member/update', { team_id: id, user_id: uid, is_team_admin: !!isTeamAdmin }, 'Failed to update member');
        return true;
    });
}

export async function removeTeamMember(teamID, userID) {
    const id = Number(teamID || 0);
    const uid = Number(userID || 0);
    if (!id || !uid) throw new Error('team_id and user_id required');
    return enqueueApiCall(async () => {
        await postApiData('/api/v1/team/member/remove', { team_id: id, user_id: uid }, 'Failed to remove member');
        return true;
    });
}

function writeTeamMember(url, teamID, email, isTeamAdmin) {
    const id = Number(teamID || 0);
    const cleanEmail = String(email || '').trim().toLowerCase();
    if (!id || !cleanEmail) throw new Error('team_id and email required');
    return enqueueApiCall(async () => {
        await postApiData(url, { team_id: id, email: cleanEmail, is_team_admin: !!isTeamAdmin }, 'Failed to update member');
        return true;
    });
}

export async function getBioAgentModels() {
    return getApiData(withImpersonation('/api/v1/bio-agent/models'), 'Failed to load Bio Agent models');
}

export async function listBioAgentThreads(group = 'all', project = '') {
    const params = new URLSearchParams();
    if (group) params.set('group', String(group));
    if (project) params.set('project', String(project).trim());
    params.set('limit', '80');
    return getApiData(withImpersonation(`/api/v1/bio-agent/list?${params.toString()}`), 'Failed to load Bio Agents');
}

export async function getBioAgentThread(threadId) {
    const id = String(threadId || '').trim();
    if (!id) throw new Error('thread_id required');
    return getApiData(withImpersonation(`/api/v1/bio-agent/view?thread_id=${encodeURIComponent(id)}`), 'Failed to load Bio Agent');
}

export async function deleteBioAgentThread(threadId) {
    const id = String(threadId || '').trim();
    if (!id) throw new Error('thread_id required');
    return postApiData('/api/v1/bio-agent/delete', { thread_id: id }, 'Failed to delete Bio Agent');
}

export async function controlBioAgentThread(threadId, action, message = '') {
    return postApiData('/api/v1/bio-agent/control', {
        thread_id: String(threadId || '').trim(),
        action: String(action || '').trim(),
        message: String(message || ''),
    }, 'Failed to update Bio Agent');
}

function parseSSEEventBlock(block) {
    if (!block) return null;
    const lines = String(block).split('\n');
    let event = 'message';
    const dataLines = [];

    for (const lineRaw of lines) {
        const line = String(lineRaw || '');
        if (!line || line.startsWith(':')) continue;
        if (line.startsWith('event:')) {
            event = line.slice(6).trim() || 'message';
            continue;
        }
        if (line.startsWith('data:')) {
            const payload = line.slice(5).replace(/^\s/, '');
            dataLines.push(payload);
        }
    }

    if (!dataLines.length) return null;
    const rawData = dataLines.join('\n');
    let data = rawData;
    try {
        data = JSON.parse(rawData);
    } catch (_) {}
    return { event, data };
}

export async function runBioAgentStream({ messages = [], provider = '', project = '', viewContext = null, threadId = '', message = '', onEvent = null } = {}) {
    const safeMessages = Array.isArray(messages) ? messages : [];
    const safeContext = (viewContext && typeof viewContext === 'object' && !Array.isArray(viewContext))
        ? viewContext
        : null;
    const response = await fetch(withImpersonation('/api/v1/bio-agent/chat'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            thread_id: String(threadId || '').trim(),
            message: String(message || '').trim(),
            messages: safeMessages,
            provider: String(provider || '').trim(),
            project: String(project || '').trim(),
            view_context: safeContext,
            stream: true,
        }),
    });

        if (!response.ok) {
            let msg = `Bio Agent request failed (HTTP ${response.status})`;
            try {
                const payload = await response.json();
                if (payload && payload.error) msg = String(payload.error);
            } catch (_) {}
            throw new Error(msg);
        }

        if (!response.body) {
            throw new Error('Bio Agent endpoint did not return a stream');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let donePayload = null;

        const emit = (evt) => {
            if (!evt || typeof onEvent !== 'function') return;
            try { onEvent(evt); } catch (_) {}
        };

        while (true) {
            const { value, done } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n');

            let boundary = buffer.indexOf('\n\n');
            while (boundary !== -1) {
                const block = buffer.slice(0, boundary);
                buffer = buffer.slice(boundary + 2);

                const evt = parseSSEEventBlock(block);
                if (evt) {
                    emit(evt);
                    if (evt.event === 'done') {
                        donePayload = evt.data || {};
                    } else if (evt.event === 'error') {
                        const errMsg = (evt.data && typeof evt.data === 'object' && evt.data.message)
                            ? String(evt.data.message)
                            : 'Bio Agent stream failed';
                        throw new Error(errMsg);
                    }
                }
                boundary = buffer.indexOf('\n\n');
            }
        }

        const tail = decoder.decode();
        if (tail) {
            buffer += tail.replace(/\r\n/g, '\n');
        }
        if (buffer.trim()) {
            const evt = parseSSEEventBlock(buffer);
            if (evt) {
                emit(evt);
                if (evt.event === 'done') {
                    donePayload = evt.data || {};
                } else if (evt.event === 'error') {
                    const errMsg = (evt.data && typeof evt.data === 'object' && evt.data.message)
                        ? String(evt.data.message)
                        : 'Bio Agent stream failed';
                    throw new Error(errMsg);
                }
            }
        }

    if (!donePayload || typeof donePayload !== 'object') {
        throw new Error('Bio Agent stream ended before completion');
    }
    return donePayload;
}

export async function getAnalyticsSummary(filters = {}) {
    const { start, end } = filters || {};
    const params = new URLSearchParams();
    if (start) params.set('start', start);
    if (end) params.set('end', end);
    const qs = params.toString();
    return enqueueApiCall(() => getProtectedApiData(
        `/api/v1/admin/analytics${qs ? `?${qs}` : ''}`,
        'Failed to load analytics',
        {},
    ));
}

function analyticsFilterParams(filters = {}, { includeSort = false } = {}) {
    const params = new URLSearchParams();
    const { start, end, userEmail, excludeUserEmail, excludeUserEmails, ip, excludeIp } = filters || {};
    if (start) params.set('start', start);
    if (end) params.set('end', end);
    if (userEmail) params.set('user_email', userEmail);
    const excludedEmails = Array.isArray(excludeUserEmails)
        ? excludeUserEmails
        : (excludeUserEmail ? [excludeUserEmail] : []);
    excludedEmails
        .map((email) => String(email || '').trim())
        .filter(Boolean)
        .forEach((email) => params.append('exclude_user_email', email));
    if (ip) params.set('ip', ip);
    if (excludeIp) params.set('exclude_ip', excludeIp);
    if (includeSort) {
        const { sort, limit } = filters || {};
        if (sort) params.set('sort', sort);
        if (limit) params.set('limit', String(limit));
    }
    return params;
}

export async function getAnalyticsTraffic(filters = {}) {
    const params = analyticsFilterParams(filters);
    const qs = params.toString();

    return enqueueApiCall(() => getProtectedApiData(
        `/api/v1/admin/analytics/traffic${qs ? `?${qs}` : ''}`,
        'Failed to load traffic analytics',
        { buckets: [], endpoint_totals: [], total_hits: 0 },
    ));
}

export async function getAnalyticsClientInfo() {
    return enqueueApiCall(() => getProtectedApiData(
        '/api/v1/admin/analytics/client',
        'Failed to load analytics client info',
        {},
    ));
}

export async function getAnalyticsUsers(filters = {}) {
    const params = analyticsFilterParams(filters, { includeSort: true });
    const qs = params.toString();

    return enqueueApiCall(async () => {
        const data = await getProtectedApiData(
            `/api/v1/admin/analytics/users${qs ? `?${qs}` : ''}`,
            'Failed to load user activity analytics',
            [],
        );
        return Array.isArray(data) ? data : [];
    });
}

export async function addCredits(amount) {
    if (isNaN(amount) || amount < 1) return;
    
    return enqueueApiCall(async () => {

        try {
            const response = await fetch(withImpersonation('/api/v1/payment/create-checkout'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ credits: amount })
            });
            
            const resp = await response.json();
            if (resp.success && resp.data.url) {
                window.location.href = resp.data.url;
            } else {
                console.error('Payment creation failed:', resp.error);
            }
        } catch (error) {
            console.error('Payment error:', error);
        }
    })
}

export async function applyPromo(code) {
    const promo = String(code || '').trim();
    if (!promo) throw new Error('Promo code required');

    return enqueueApiCall(async () => {
        const response = await fetch(withImpersonation('/api/v1/promo/apply'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ code: promo }),
        });

        const resp = await response.json();
        if (!resp || !resp.success) {
            throw new Error((resp && resp.error) || 'Failed to apply promo');
        }
        return resp.data || {};
    });
}

export async function loadTxs(offset = 0) {
    return enqueueApiCall(async () => {
        if (state.txsLoading) return;
        state.txsLoading = true;

        try {
            const status = state.txFilter;
            const url = withImpersonation(`/api/v1/tx/list?limit=${MAX_DATA_ENTRIES}&offset=${offset}&status=${status}`);
            const data = await getApiData(url, 'Failed to fetch transactions', { txs: [], total: 0 });
            state.txs = data.txs || [];
            state.totalTxs = data.total || 0;
        } catch (error) {
            console.error('Failed to fetch transactions:', error);
        } finally {
            state.txsLoading = false;
        }
    });
}

export async function loadJobs(limit = MAX_DATA_ENTRIES, offset = 0, project = '', before = '', program = '', pipeline = '') {
    return runReadApiCall(async () => {
        if (state.jobsLoading) return;
        state.jobsLoading = true;
        
        try {
            const programFilter = program || state.jobProgramFilter || '';
            const pipelineFilter = pipeline || state.jobPipelineFilter || '';
            state.jobProgramFilter = programFilter;
            state.jobPipelineFilter = pipelineFilter;
            const data = await listJobs(limit, offset, project, state.jobFilter, before, programFilter, pipelineFilter);
            state.totalJobs = data.total || 0;
            state.jobs = state.totalJobs == 0 ? [] : (data.jobs || []);
        } catch (error) {
            console.error('Failed to fetch jobs:', error);
        } finally {
            state.jobsLoading = false;
        }
    });
}

// List jobs without mutating global state (used by lightweight views)
export async function listJobs(limit = 20, offset = 0, project = '', status = 'all', before = '', program = '', pipeline = '') {
    const statusFilter = status || 'all';
    const projQS = project ? `&project=${encodeURIComponent(project)}` : '';
    const beforeQS = before ? `&before=${encodeURIComponent(before)}` : '';
    const programQS = program ? `&program=${encodeURIComponent(program)}` : '';
    const pipelineQS = pipeline ? `&pipeline_id=${encodeURIComponent(pipeline)}&pipeline=${encodeURIComponent(pipeline)}` : '';
    const url = withImpersonation(`/api/v1/job/list?limit=${limit}&offset=${offset}&status=${encodeURIComponent(statusFilter)}${projQS}${beforeQS}${programQS}${pipelineQS}`);
    return getApiData(url, 'Failed to list jobs', { total: 0, jobs: [] });
}

export async function listPipelines(limit = 20, offset = 0, status = 'all', project = '') {
    const statusFilter = status || 'all';
    const statusQS = statusFilter && statusFilter !== 'all' ? `&status=${encodeURIComponent(statusFilter)}` : '';
    const projQS = project ? `&project=${encodeURIComponent(project)}` : '';
    const url = withImpersonation(`/api/v1/pipeline/list?limit=${limit}&offset=${offset}${statusQS}${projQS}`);
    return getApiData(url, 'Failed to list pipelines', { total: 0, pipelines: [] });
}

export async function getPipelineInfo(pipelineID) {
    if (!pipelineID) throw new Error('pipeline_id required');
    const url = withImpersonation(`/api/v1/pipeline/info?pipeline_id=${encodeURIComponent(pipelineID)}`);
    return getApiData(url, 'Failed to load pipeline');
}

export async function listTemplates(limit = 20, offset = 0) {
    const url = withImpersonation(`/api/v1/template/list?limit=${limit}&offset=${offset}`);
    return getApiData(url, 'Failed to list templates', { total: 0, templates: [] });
}

export async function getTemplateInfo(templateID) {
    if (!templateID) throw new Error('template_id required');
    const url = withImpersonation(`/api/v1/template/info?template_id=${encodeURIComponent(templateID)}`);
    return getApiData(url, 'Failed to load template');
}

export async function saveTemplate(payload = {}) {
    return enqueueApiCall(() => postApiData('/api/v1/template/save', payload || {}, 'Failed to save template'));
}

export async function deleteTemplate(templateID) {
    if (!templateID) throw new Error('template_id required');
    return enqueueApiCall(() => postApiData('/api/v1/template/delete', { template_id: templateID }, 'Failed to delete template'));
}

export async function submitPipeline(payload = {}) {
    return enqueueApiCall(async () => {
        const data = await postApiData('/api/v1/pipeline/submit', payload, 'Failed to submit pipeline');
        clearProjectsCache();
        return data;
    });
}

export async function loadJobInfo(jobID) {
    return enqueueApiCall(async () => {
        const normalized = normalizeJobID(jobID);
        if (!isValidJobID(normalized)) {
            console.error("Invalid job ID");
            return;
        }
        if (state.jobInfoLoading) return;
        state.jobInfoLoading = true;

        try {
            state.jobinfo = await getJobInfo(normalized);
            state.success = true;
            state.error = null;
        } catch (error) {
            state.success = false;
            state.error = error.message || "Network error occurred";
            console.error("Failed to fetch job info:", error);
        } finally {
            state.jobInfoLoading = false;
        }
    });
}

export async function cancelJob(jobId) {

    return enqueueApiCall(async () => {

        if (state.jobCanceling) return;
        state.jobCanceling = true;

        try {
            await postApiData(`/api/v1/job/cancel?jobid=${jobId}`, undefined, 'Failed to cancel job');
            clearProjectsCache();
            return true;
        } catch (error) {
            console.error('Error cancelling job:', error);
            throw error;
        } finally {
            state.jobCanceling = false;
        }
    })
}

export async function rerunJob(jobId) {
    return enqueueApiCall(async () => {
        const normalized = normalizeJobID(jobId);
        if (!isValidJobID(normalized)) throw new Error('Invalid job ID');
        const data = await postApiData('/api/v1/job/rerun', { job_id: normalized }, 'Failed to rerun job');
        clearProjectsCache();
        return data;
    });
}

function submitJobForm(endpoint, formData, { parseError, logLabel }) {
    return enqueueApiCall(async () => {
        if (state.jobSubmitting) return;
        state.jobSubmitting = true;

        state.success = false;
        state.error = '';
        state.job_id = '';

        try {
            const data = await postApiData(endpoint, formData, parseError);
            const jobId = data?.job_id || data?.jobid || '';
            if (!jobId) throw new Error("An unknown error occurred");
            state.job_id = jobId;
            state.success = true;
            state.error = null;
            clearProjectsCache();
        } catch (error) {
            state.success = false;
            state.error = error.message || parseError;
            console.error(logLabel, error);
        } finally {
            state.jobSubmitting = false;
        }
    });
}

export async function submitJob(formData) {
    return submitJobForm('/api/v1/job/submit', formData, {
        parseError: 'An error occurred during job submission',
        logLabel: 'Failed to submit job:',
    });
}

export async function submitAdvancedJob(formData) {
    return submitJobForm('/api/v1/job/submit-advanced', formData, {
        parseError: 'An error occurred during advanced job submission',
        logLabel: 'Failed to submit advanced job:',
    });
}

// Move a single job to a project (empty projectName unsets it)
export async function moveJobToProject(jobId, projectName = '') {
    return enqueueApiCall(async () => {
        if (!jobId) throw new Error('jobId required');
        await postApiData('/api/v1/job/move-project', { job_id: jobId, project: projectName }, 'Failed to move job');
        clearProjectsCache();
        return true;
    });
}

// Update a job memo (empty memo clears it)
export async function updateJobMemo(jobId, memo = '') {
    return enqueueApiCall(async () => {
        if (!jobId) throw new Error('jobId required');
        await postApiData('/api/v1/job/update-memo', { job_id: jobId, memo }, 'Failed to update memo');
        return true;
    });
}

// Convenience single-job API used by viewjob.js
// (legacy alias removed; use moveJobToProject above)

export async function listProjects({ force = false } = {}) {
    const now = Date.now();
    const cacheScope = currentProjectsCacheScope();
    const url = withImpersonation('/api/v1/project/list?limit=100&offset=0');
    if (!force && projectsCache && projectsCacheScope === cacheScope && (now - projectsCacheAt) < PROJECTS_CACHE_TTL_MS) {
        return projectsCache.slice();
    }
    return runReadApiCall(async () => {
        try {
            const data = await getApiData(url, 'Failed to load projects', { total: 0, projects: [] });
            projectsCache = Array.isArray(data.projects) ? data.projects : [];
            projectsCacheAt = Date.now();
            projectsCacheScope = cacheScope;
            return projectsCache.slice();
        } catch (err) {
            console.error('Failed to load projects:', err);
            throw err;
        }
    });
}

// Paginated, searchable project list for the Projects page.
export async function listProjectsPage(limit = 20, offset = 0, q = '') {
    const qs = new URLSearchParams();
    qs.set('limit', String(limit));
    qs.set('offset', String(offset));
    if (q) qs.set('q', q);
    const url = withImpersonation(`/api/v1/project/list?${qs.toString()}`);
    return getApiData(url, 'Failed to load projects', { total: 0, projects: [] });
}

export async function createProject(name) {
    return enqueueApiCall(async () => {
        await postApiData('/api/v1/project/new', { name }, 'Failed to create project');
        clearProjectsCache();
        return true;
    });
}

export async function deleteProject(name) {
    return enqueueApiCall(async () => {
        await postApiData('/api/v1/project/delete', { name }, 'Failed to delete project');
        clearProjectsCache();
        return true;
    });
}

export async function renameProject(name, newName) {
    const cleanName = String(name || '').trim();
    const cleanNewName = String(newName || '').trim();
    if (!cleanName || !cleanNewName) throw new Error('name and new_name required');
    return enqueueApiCall(async () => {
        await postApiData('/api/v1/project/rename', { name: cleanName, new_name: cleanNewName }, 'Failed to rename project');
        clearProjectsCache();
        return true;
    });
}

export async function downloadJobData(jobid, { acceptPartial = false } = {}) {
    const normalized = normalizeJobID(jobid);
    if (!isValidJobID(normalized)) return;

    const qs = new URLSearchParams({ jobid: normalized });
    if (acceptPartial) qs.set('accept_partial_data', 'true');
    const url = withImpersonation(`/api/v1/job/download?${qs.toString()}`);
    triggerDownload(url, `${normalized}.zip`);
}

export function downloadDataFile({ jobid = '', digest = '', path = '' } = {}) {
    if (!path) return false;
    const qs = new URLSearchParams();
    if (jobid) qs.set('jobid', jobid);
    else if (digest) qs.set('digest', digest);
    else return false;
    qs.set('path', path);
    const url = withImpersonation(jobid
        ? `/api/v1/job/file?${qs.toString()}`
        : `/api/v1/dataset/file?${qs.toString()}`);
    triggerDownload(url, path.split('/').filter(Boolean).pop() || 'download', { rel: 'noopener' });
    return true;
}

const PRESIGN_TYPES = ['job_download', 'job_file', 'dataset_download', 'dataset_file'];
const MAX_PRESIGN_TTL_SECONDS = 3 * 24 * 3600; // 3 days; UI enforces the same cap

export async function mintPresignedDownload(opts = {}) {
    return enqueueApiCall(async () => {
        const { type } = opts || {};
        if (!type || !PRESIGN_TYPES.includes(type)) {
            throw new Error('invalid presign type');
        }
        let ttlSeconds = Number(opts.ttl_seconds || opts.ttlSeconds || 0);
        if (!Number.isFinite(ttlSeconds) || ttlSeconds <= 0) ttlSeconds = 3600;
        if (ttlSeconds > MAX_PRESIGN_TTL_SECONDS) ttlSeconds = MAX_PRESIGN_TTL_SECONDS;

        const body = {
            type,
            jobid: opts.jobid || opts.jobId || '',
            digest: opts.digest || '',
            name: opts.name || '',
            project: opts.project || '',
            path: opts.path || '',
            accept_partial_data: !!opts.accept_partial_data,
            ttl_seconds: Math.round(ttlSeconds),
        };
        if (state.isOwner && state.viewAsEmail) {
            body.user_email = state.viewAsEmail;
        }

        return postApiData('/api/v1/download/presign', body, 'Failed to mint URL');
    });
}

// -------- API Keys --------

export async function listAPIKeys() {
    return enqueueApiCall(() => getApiData(withImpersonation('/api/v1/key/list'), 'Failed to load API keys', []));
}

export async function createAPIKey({ label = '', expiresInDays = 0, access = 'read/write', project = '' } = {}) {
    return enqueueApiCall(async () => {
        const data = await postApiData('/api/v1/key/new', { label, expires_in_days: expiresInDays, access, project }, 'Failed to create API key');
        if (data && data.key) return data.key; // plaintext key (shown once)
        throw new Error('Failed to create API key');
    });
}

export async function deleteAPIKey(keyHash) {
    return enqueueApiCall(async () => {
        await postApiData('/api/v1/key/delete', { key_hash: keyHash }, 'Failed to delete API key');
        return true;
    });
}

export async function listOAuthTokens() {
    return enqueueApiCall(() => getApiData(withImpersonation('/api/v1/oauth/token/list'), 'Failed to load OAuth tokens', []));
}

export async function revokeOAuthToken(tokenHash) {
    return enqueueApiCall(async () => {
        await postApiData('/api/v1/oauth/token/revoke', { token_hash: tokenHash }, 'Failed to revoke OAuth token');
        return true;
    });
}

// -------- Programs --------
// Simple in-memory cache to keep UI snappy across pages
let _programsCache = null;
let _programsCacheAt = 0;
const _PROGRAMS_TTL_MS = 5 * 60 * 1000; // 5 minutes

export async function listPrograms({ force = false } = {}) {
    const now = Date.now();
    if (!force && _programsCache && (now - _programsCacheAt) < _PROGRAMS_TTL_MS) {
        return _programsCache;
    }
    const data = await getApiData('/api/v1/program/list', 'Failed to list programs', []);
    const list = Array.isArray(data) ? data : [];
    _programsCache = list;
    _programsCacheAt = Date.now();
    return list;
}

const _programParamsCache = new Map();
const _programParamsPending = new Map();

export async function getProgramParams(program, { force = false } = {}) {
    const name = String(program || '').trim();
    if (!name) throw new Error('program required');
    if (!force && _programParamsCache.has(name)) {
        return _programParamsCache.get(name);
    }
    if (!force && _programParamsPending.has(name)) {
        return _programParamsPending.get(name);
    }
    const request = getApiData(
        `/api/v1/program/params?program=${encodeURIComponent(name)}`,
        'Failed to load program params'
    ).then((payload) => {
        _programParamsCache.set(name, payload);
        return payload;
    }).finally(() => {
        _programParamsPending.delete(name);
    });
    if (!force) _programParamsPending.set(name, request);
    return request;
}

const _metricsCache = new Map();
const _metricsPending = new Map();
const _METRICS_TTL_MS = 5 * 60 * 1000;

export async function getMetrics({ program = '', params = null, force = false } = {}) {
    const name = String(program || '').trim().toLowerCase();
    const paramsJSON = params && typeof params === 'object' ? JSON.stringify(params) : '';
    const key = `${name || '__all__'}:${paramsJSON}`;
    const now = Date.now();
    const cached = _metricsCache.get(key);
    if (!force && cached && (now - cached.at) < _METRICS_TTL_MS) {
        return cached.value;
    }
    if (!force && _metricsPending.has(key)) {
        return _metricsPending.get(key);
    }
    const query = new URLSearchParams();
    if (name) query.set('program', name);
    if (paramsJSON) query.set('params', paramsJSON);
    const suffix = query.toString() ? `?${query.toString()}` : '';
    const request = getApiData(`/api/v1/metrics${suffix}`, 'Failed to load metrics', [])
        .then((payload) => {
            const list = Array.isArray(payload) ? payload : [];
            _metricsCache.set(key, { value: list, at: Date.now() });
            return list;
        })
        .finally(() => {
            _metricsPending.delete(key);
        });
    if (!force) _metricsPending.set(key, request);
    return request;
}

// Global pricing
let _pricingCache = null;
let _pricingCacheAt = 0;
const _PRICING_TTL_MS = 5 * 60 * 1000;

export async function getPricing({ force = false } = {}) {
    const now = Date.now();
    if (!force && _pricingCache && (now - _pricingCacheAt) < _PRICING_TTL_MS) {
        return _pricingCache;
    }
    _pricingCache = await getApiData('/api/v1/pricing', 'Failed to load pricing', { submit_fee: 0 });
    _pricingCacheAt = Date.now();
    return _pricingCache;
}

// -------- Datasets --------

export async function listDatasets(limit = 20, offset = 0, project = '') {
    const projQS = project ? `&project=${encodeURIComponent(project)}` : '';
    const url = withImpersonation(`/api/v1/dataset/list?limit=${limit}&offset=${offset}${projQS}`);
    return getApiData(url, 'Failed to list datasets', { total: 0, datasets: [] });
}

export async function getDatasetHistory(name, project = '', limit = 200, offset = 0) {
    const n = String(name || '').trim();
    if (!n) throw new Error('name required');
    const projQS = project ? `&project=${encodeURIComponent(project)}` : '';
    const url = withImpersonation(`/api/v1/dataset/history?name=${encodeURIComponent(n)}&limit=${limit}&offset=${offset}${projQS}`);
    return getApiData(url, 'Failed to load dataset history', { total: 0, datasets: [] });
}

export async function deleteDataset(digest) {
    return enqueueApiCall(async () => {
        if (!digest) throw new Error('digest required');
        await postApiData(`/api/v1/dataset/delete?digest=${encodeURIComponent(digest)}`, undefined, 'Failed to delete dataset');
        clearProjectsCache();
        return true;
    });
}

export async function createDataset(formData) {
    return enqueueApiCall(async () => {
        const data = await postApiData('/api/v1/dataset/new', formData, 'Failed to create dataset');
        clearProjectsCache();
        return data;
    });
}

export async function editDataset(formData) {
    return enqueueApiCall(async () => {
        const data = await postApiData('/api/v1/dataset/edit', formData, 'Failed to edit dataset');
        clearProjectsCache();
        return data;
    });
}

export async function moveDatasetToProject(digest, projectName = '') {
    return enqueueApiCall(async () => {
        if (!digest) throw new Error('digest required');
        await postApiData('/api/v1/dataset/move-project', { digest, project: projectName }, 'Failed to move dataset');
        clearProjectsCache();
        return true;
    });
}

export async function renameDatasetLineage(name, newName) {
    return enqueueApiCall(async () => {
        const source = String(name || '').trim();
        const target = String(newName || '').trim();
        if (!source || !target) throw new Error('name and new name required');
        await postApiData('/api/v1/dataset/rename', { name: source, new_name: target }, 'Failed to rename dataset');
        clearProjectsCache();
        return true;
    });
}

// Trigger dataset archive download by digest (preferred) or name
export async function downloadDatasetArchive({ digest, name, project } = {}) {
    try {
        let url = '';
        if (digest) {
            url = withImpersonation(`/api/v1/dataset/download?digest=${encodeURIComponent(digest)}`);
        } else if (name) {
            url = withImpersonation(`/api/v1/dataset/download?name=${encodeURIComponent(name)}`);
        } else {
            throw new Error('dataset identifier required');
        }
        const fnameId = digest || name || 'dataset';
        triggerDownload(url, `dataset_${fnameId}.zip`, { target: '_self' });
        // No fallback navigation to avoid unexpected redirects/rate-limit hits
        return true;
    } catch (err) {
        throw err;
    }
}

// Upload dataset with progress callback
// onProgress receives { loaded, total, percent }
export function createDatasetUpload(formData, onProgress) {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', withImpersonation('/api/v1/dataset/new'));
    xhr.withCredentials = true; // include cookies/session

    const promise = new Promise((resolve, reject) => {
        try {
            xhr.upload.onprogress = (e) => {
                if (typeof onProgress === 'function') {
                    const loaded = e.loaded || 0;
                    const total = e.lengthComputable ? e.total : (formData && formData.getAll ? formData.getAll('folder').reduce((acc, f) => acc + (f?.size || 0), 0) : 0);
                    const percent = total > 0 ? Math.min(100, Math.round((loaded / total) * 100)) : 0;
                    try { onProgress({ loaded, total, percent }); } catch {}
                }
            };

            xhr.onerror = () => {
                const e = new Error('Network error during dataset upload');
                reject(e);
            };
            xhr.onabort = () => {
                const e = new Error('Upload canceled');
                e.canceled = true;
                reject(e);
            };
            xhr.onload = () => {
                try {
                    const status = xhr.status;
                    const ct = xhr.getResponseHeader('Content-Type') || '';
                    let data = {};
                    if (ct.includes('application/json')) {
                        data = JSON.parse(xhr.responseText || '{}');
                    } else if ((xhr.responseText || '').trim().startsWith('{')) {
                        data = JSON.parse(xhr.responseText || '{}');
                    }
                    if (status >= 200 && status < 300 && data && data.success) {
                        clearProjectsCache();
                        resolve(data.data || {});
                    } else {
                        const err = new Error((data && data.error) || `Upload failed (${status})`);
                        if (data && data.data) err.data = data.data;
                        reject(err);
                    }
                } catch (e) {
                    reject(new Error('Invalid server response'));
                }
            };

            xhr.send(formData);
        } catch (err) {
            reject(err);
        }
    });

    return { promise, cancel: () => { try { xhr.abort(); } catch {} } };
}
